import { AvatarSize } from "@components/Avatar";
import { NonprofitAvatar } from "@components/Avatar/NonprofitAvatar";
import {
  TagIllustrationLabel,
  TagLink,
} from "@components/TagIllustrationLabel";
import { useAllCauses } from "@components/Tags/TagSelector";
import { OpenPlaidModalParams } from "@components/donate/ConnectAccountWithPlaid";
import { AddNewCardPaymentWidget } from "@components/donate/DonateV3/PaymentProcess/components/PaymentMethod";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { UUID } from "io-ts-types/UUID";
import React, { useCallback, useState, useEffect, useMemo } from "react";

import { CurrencyValue } from "@every.org/common/src/codecs/currency";
import {
  NonprofitResponse,
  PaymentSourceResponse as PaymentSource,
  PersonalGivingCreditResponse,
} from "@every.org/common/src/codecs/entities";
import { colorPalette } from "@every.org/common/src/display/palette";
import { FUND_ACCOUNT_NONPROFIT_SLUG } from "@every.org/common/src/entity/constants";
import {
  PaymentMethod,
  PaymentSourceStatus,
} from "@every.org/common/src/entity/types";
import {
  ClientRouteName,
  getRoutePath,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";
import { minimumDenominationAmountToCurrencyValue } from "@every.org/common/src/helpers/currency";
import {
  setDefaultPaymentSourceRouteSpec,
  detachPaymentSourceRouteSpec,
  GetPaymentSourcesResponse,
  getPaymentSourcesRouteSpec,
} from "@every.org/common/src/routes/donate";

import { Button, ButtonTargetKind, ButtonRole } from "src/components/Button";
import { Card } from "src/components/Card";
import { ErrorMessage } from "src/components/ErrorMessage";
import { Icon, IconSize, IconDisplay } from "src/components/Icon";
import { Link } from "src/components/Link";
import { Loading, LoadingIcon } from "src/components/LoadingIndicator";
import { Modal } from "src/components/Modal";
import { NonprofitLink } from "src/components/NonprofitLink";
import { UserLink } from "src/components/UserLink";
import {
  bankPlaceholderItem,
  PaymentMethodItem,
  PaymentMethodList,
  paymentItemCss,
  USE_BANK_ACCOUNT_PROMPT,
  AddBankPaymentItem,
  AddCardPaymentItem,
} from "src/components/donate/PaymentMethod";
import { generateModalLink } from "src/components/layout/DonateButton";
import { PageSection } from "src/components/layout/PageSection";
import { SecondaryText } from "src/components/textHelpers";
import { useLoggedInUser } from "src/context/AuthContext/hooks";
import { fetchGivingCredits } from "src/context/DonationsContext/actions";
import { getMessageForError } from "src/errors";
import { useAsyncEffect } from "src/hooks/useAsyncEffect";
import { useEdoRouter } from "src/hooks/useEdoRouter";
import { FundAccountCard } from "src/pages/Manage/FundAccountCard";
import { NonprofitWarningCard } from "src/pages/Manage/NonprofitWarningCard";
import {
  ManagePageLayout,
  ManagePageTab,
  ManageSectionHeader,
} from "src/pages/Manage/index";
import { LinkAppearance } from "src/styles/link";
import { MAX_TEXT_INPUT_WIDTH_VAR } from "src/theme/GlobalStyle/inputStyles";
import { colorCssVars } from "src/theme/color";
import { roundedBorder } from "src/theme/common";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import {
  horizontalStackCss,
  spacing,
  verticalStackCss,
} from "src/theme/spacing";
import { FontWeight } from "src/theme/text";
import { queryApi } from "src/utility/apiClient";
import { logger } from "src/utility/logger";

export const FUND_ACCOUNT_FRAGMENT_ID = "fund";

const rotateCss = css`
  position: relative;
  top: -1px;
  transform: rotate(180deg);
`;

const GiftCardDescription = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  :not(:last-child) {
    margin-bottom: ${spacing.xs};
  }
`;

const GiftCardDescriptionLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const GiftCardLabel = styled.div`
  width: 100%;
  flex-wrap: wrap;
  align-items: center;
`;

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  max-width: var(${MAX_TEXT_INPUT_WIDTH_VAR});
  padding: ${spacing.m};
  > *:not(:last-child) {
    margin-bottom: ${spacing.xs};
  }
`;

const ManageStyledCard = styled(Card)`
  padding: ${spacing.xl};
`;

const GiftCardPaymentItem: React.FCC<{
  givingCredit: PersonalGivingCreditResponse;
}> = ({ givingCredit }) => {
  const {
    currency,
    createdAt,
    amountRemaining,
    expires,
    fromUser,
    fromNonprofit,
    restrictedToNonprofits,
    restrictedToCauses,
  } = givingCredit;

  const restrictedToCausesIds = restrictedToCauses?.map((c) => c.id);

  const fromLink = () => {
    if (fromUser) {
      return <UserLink user={fromUser} />;
    }

    if (fromNonprofit) {
      return (
        <NonprofitLink
          data-tname={"givingCredit-fromNonprofitLink"}
          nonprofit={fromNonprofit}
        />
      );
    }

    return "Every.org";
  };

  return (
    <div
      css={[
        paymentItemCss,
        roundedBorder,
        { border: `1px solid ${colorPalette.grayLight}` },
      ]}
    >
      <GiftCardLabel>
        <GiftCardDescription>
          <GiftCardDescriptionLine>
            <div css={[horizontalStackCss.xs, { alignItems: "center" }]}>
              <Icon
                iconImport={() =>
                  import("src/components/Icon/icons/GiftCardIcon")
                }
                size={IconSize.MEDIUM}
                display={IconDisplay.ACCENT}
              />{" "}
              <div css={verticalStackCss.xxxs}>
                <div css={[horizontalStackCss.xxs, { alignItems: "center" }]}>
                  <p>From:</p>
                  <p>{fromLink()}</p>
                </div>
                <SecondaryText>
                  on {createdAt.toLocaleDateString()}
                </SecondaryText>
              </div>
            </div>
            <div css={[verticalStackCss.none, { textAlign: "end" }]}>
              <p>
                $
                {minimumDenominationAmountToCurrencyValue({
                  currency,
                  amountInMinDenom: amountRemaining,
                }).amount.toString()}{" "}
                remaining
              </p>
              <SecondaryText>
                {!expires || expires > new Date() ? "expires" : "expired"}:{" "}
                {expires ? expires.toLocaleString() : "never"}
              </SecondaryText>
            </div>
          </GiftCardDescriptionLine>
        </GiftCardDescription>
        <GiftCardRestrictions
          restrictedToNonprofits={restrictedToNonprofits}
          restrictedToCausesIds={restrictedToCausesIds}
        />
      </GiftCardLabel>
    </div>
  );
};

const GiftCardRestrictions = (props: {
  restrictedToNonprofits?: NonprofitResponse[];
  restrictedToCausesIds?: UUID[];
}) => {
  const [isOpen, setOpen] = useState(false);

  const { restrictedToNonprofits, restrictedToCausesIds } = props;
  const allCauses = useAllCauses();

  const restrictedToCauses = useMemo(
    () =>
      restrictedToCausesIds
        ? allCauses.filter(({ id }) => restrictedToCausesIds.includes(id))
        : undefined,
    [allCauses, restrictedToCausesIds]
  );

  if (
    (!restrictedToNonprofits && !restrictedToCauses) ||
    (!restrictedToNonprofits?.length && !restrictedToCauses?.length)
  ) {
    return null;
  }

  return (
    <div>
      <Button
        data-tname="GiftCardRestrictions--open"
        role={ButtonRole.UNSTYLED}
        onClick={{
          kind: ButtonTargetKind.FUNCTION,
          action: () => {
            setOpen(!isOpen);
          },
        }}
        contentCss={css`
          font-weight: ${FontWeight.MEDIUM};
          ${horizontalStackCss.xxs};
          align-items: center;
        `}
      >
        <span>Restrictions</span>{" "}
        <Icon
          css={isOpen && rotateCss}
          iconImport={() => import("@components/Icon/icons/SelectArrowIcon")}
          size={IconSize.X_SMALL}
          display={IconDisplay.SECONDARY}
        />
      </Button>
      {isOpen && (
        <div css={verticalStackCss.s}>
          <p css={{ color: `var(${colorCssVars.text.secondary})` }}>
            This gift card can only be used to donate to the following
            nonprofits and causes:
          </p>
          <div
            css={css`
              display: grid;
              grid-template-columns: 1fr;
              gap: ${spacing.s};

              ${cssForMediaSize({
                min: MediaSize.MEDIUM,
                css: css`
                  grid-template-columns: 1fr 1fr;
                `,
              })}
            `}
          >
            <ul css={verticalStackCss.xs}>
              {restrictedToNonprofits?.map((np) => (
                <li key={np.id} css={{ display: "flex" }}>
                  <NonprofitLink
                    data-tname="giftCarfRestrictionToNonprofit__Link"
                    nonprofit={np}
                    css={[horizontalStackCss.xs]}
                    appearance={LinkAppearance.HYPERLINK_UNCOLORED}
                  >
                    <NonprofitAvatar
                      size={AvatarSize.XX_SMALL}
                      nonprofit={np}
                    />
                    <span>{np.name}</span>
                  </NonprofitLink>
                </li>
              ))}
            </ul>

            <ul css={verticalStackCss.xs}>
              {restrictedToCauses?.map((cause) => (
                <li key={cause.id} css={{ display: "flex" }}>
                  <TagLink
                    data-tname="giftCarfRestrictionToCause__Link"
                    tagName={cause.tagName}
                    appearance={LinkAppearance.HYPERLINK_UNCOLORED}
                  >
                    <TagIllustrationLabel
                      size={AvatarSize.XX_SMALL}
                      tag={cause}
                      uncolored
                    />
                  </TagLink>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

const GiftCards: React.FCC<{
  givingCredits?: PersonalGivingCreditResponse[];
  givingCreditsRemaining?: CurrencyValue;
  fetchGivingCreditDataError?: string;
}> = ({ givingCredits, fetchGivingCreditDataError }) => {
  if (!givingCredits) {
    return <Loading />;
  } else if (fetchGivingCreditDataError) {
    return <ErrorMessage text={fetchGivingCreditDataError} />;
  }

  const now = new Date();
  const gcIsActive = (gc: PersonalGivingCreditResponse) =>
    gc.amountRemaining > 0 && (gc.expires === null || gc.expires > now);
  const activeCredits = givingCredits.filter(gcIsActive);

  if (activeCredits.length === 0) {
    return <React.Fragment />;
  }

  return (
    <div css={verticalStackCss.xs}>
      <ManageSectionHeader title="Gift cards" />
      <StyledCard>
        <ul css={verticalStackCss.m}>
          {activeCredits.map((gc) => (
            <li key={gc.id}>
              <GiftCardPaymentItem givingCredit={gc} />
            </li>
          ))}
        </ul>
        <SecondaryText>
          Gift cards are automatically applied when you make a donation.{" "}
          <Link
            data-tname={"giftCardFindNonprofit"}
            to="/"
            appearance={LinkAppearance.HYPERLINK}
          >
            Find a nonprofit to support!
          </Link>
        </SecondaryText>
      </StyledCard>
    </div>
  );
};

const PaymentMethods: React.FCC = () => {
  const loggedInUser = useLoggedInUser();
  const [paymentSources, setPaymentSources] = useState<PaymentSource[]>();
  const [defaultPaymentSourceId, setDefaultPaymentSourceId] = useState<
    PaymentSource["id"] | undefined
  >();
  const [fetchPaymentSourcesError, setFetchPaymentSourcesError] =
    useState<string>();
  const [addBankAccountError, setError] = useState<string>();
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [isSelectingDefault, setIsSelectingDefault] = useState(false);
  const [newBankAccountDetailsLoading, setNewBankAccountDetailsLoading] =
    useState(false);
  const handleSources = useCallback(
    (paymentSources: GetPaymentSourcesResponse) => {
      const { sources } = paymentSources;
      setPaymentSources(sources);
      const defaultPaymentSource = sources.find((s) => s.default);
      defaultPaymentSource &&
        setDefaultPaymentSourceId(defaultPaymentSource.id);
    },
    []
  );
  const [addBankAccountTriggered, setAddBankAccountTriggered] = useState(false);
  const [addBankAccountBtn, setAddBankAccountBtn] =
    useState<HTMLElement | null>(null);

  const [pendingDeleteSourcesIds, setPendingDeleteSourcesIds] = useState<
    string[]
  >([]);
  // if add bank account is requested but button to add bank account is still
  // being mounted, this waits for it to mount and then clicks the button for
  // you.
  useEffect(
    function triggerAddBankAccount() {
      if (addBankAccountTriggered && !!addBankAccountBtn) {
        setAddBankAccountTriggered(false);
        addBankAccountBtn.click();
      }
    },
    [addBankAccountTriggered, addBankAccountBtn]
  );

  const fetchPaymentSources = useCallback(
    () =>
      queryApi(getPaymentSourcesRouteSpec, {
        body: {},
        routeTokens: {},
        queryParams: {},
      }),
    []
  );

  const handleErrors = useCallback((e: Error) => {
    setFetchPaymentSourcesError(
      getMessageForError(
        e,
        "Unable to get all payment methods. Please try again."
      )
    );
  }, []);

  useAsyncEffect({
    asyncOperation: fetchPaymentSources,
    handleResponse: handleSources,
    handleError: handleErrors,
  });

  const onPaymentSourceAdded = useCallback((paymentSource: PaymentSource) => {
    setNewBankAccountDetailsLoading(false);
    setPaymentSources((prevSources) =>
      (prevSources?.filter((s) => s.id !== paymentSource.id) || []).concat(
        paymentSource
      )
    );
    paymentSource.default && setDefaultPaymentSourceId(paymentSource.id);
    setIsAddingCard(false);
    setError(undefined);
  }, []);
  const onAddBankAccountFailed = useCallback((errorMessage: string) => {
    setNewBankAccountDetailsLoading(false);
    setError(errorMessage);
  }, []);

  const onPlaidConnectionSuccess = useCallback(() => {
    setNewBankAccountDetailsLoading(true);
    setError(undefined);
  }, []);

  const onPlaidConnectExit = useCallback(() => {
    setNewBankAccountDetailsLoading(false);
    setIsAddingCard(false);
  }, []);

  const onCompletedAddingPaymentSource = useCallback<
    OpenPlaidModalParams["onCompletedAddingAccount"]
  >(
    (result) => {
      if (result.success) {
        onPaymentSourceAdded(result.paymentSource);
      } else {
        onAddBankAccountFailed("Could not add bank account, please try again");
      }
    },
    [onPaymentSourceAdded, onAddBankAccountFailed]
  );

  const [selectedDefaultSourceId, setSelectedDefaultSourceId] =
    useState<PaymentSource["id"]>();

  useAsyncEffect({
    asyncOperation: useCallback(async () => {
      if (!selectedDefaultSourceId) {
        return;
      }
      await queryApi(setDefaultPaymentSourceRouteSpec, {
        body: {
          paymentSourceId: selectedDefaultSourceId,
        },
        routeTokens: {},
        queryParams: {},
      });
      return selectedDefaultSourceId;
    }, [selectedDefaultSourceId]),
    handleResponse: useCallback(
      (defaultSourceId: PaymentSource["id"] | undefined) => {
        if (!defaultSourceId) {
          return;
        }
        setDefaultPaymentSourceId(defaultSourceId);
        setIsSelectingDefault(false);
        setSelectedDefaultSourceId(undefined);
      },
      []
    ),
    handleError: useCallback((error) => {
      const message = "Could not set default payment method";
      logger.error({ error, message });
      setError(message);
      setIsSelectingDefault(false);
      setSelectedDefaultSourceId(undefined);
    }, []),
  });

  const deletePayment = useCallback(async (source: PaymentSource) => {
    // This is just a way out for PayPal.
    // We only show PayPal and redirect users there
    // but don't actually handle the adding or deleting methods inside paypal
    if (!source.id) {
      return;
    }
    await queryApi(detachPaymentSourceRouteSpec, {
      body: {
        paymentSourceId: source.id,
      },
      routeTokens: {},
      queryParams: {},
    });
    setPaymentSources((prevSources) =>
      prevSources?.filter((x) => x.id !== source.id)
    );
    setPendingDeleteSourcesIds((prevSourcesIds) =>
      prevSourcesIds?.filter((sourceId) => source.id !== sourceId)
    );
  }, []);

  if (!paymentSources) {
    return <Loading />;
  } else if (fetchPaymentSourcesError) {
    return <p>{fetchPaymentSourcesError}</p>;
  }

  if (isAddingCard) {
    return (
      <AddNewCardPaymentWidget
        handleConnectBankAccount={() => {
          setIsAddingCard(false);
          setAddBankAccountTriggered(true);
        }}
        onAddCancelled={() => {
          setIsAddingCard(false);
        }}
        onPaymentSourceAdded={onPaymentSourceAdded}
        cancellable
        mode="setup"
        // So confirmSetup's billing_details.name/email reflect the actual
        // logged-in donor, not blank - unlike the donate flow, this page has
        // no donor-info form fields to source them from.
        firstName={loggedInUser.firstName || undefined}
        lastName={loggedInUser.lastName || undefined}
        email={loggedInUser.email || undefined}
      />
    );
  }

  return (
    <React.Fragment>
      {addBankAccountError && (
        <ErrorMessage>{addBankAccountError}</ErrorMessage>
      )}
      <PaymentMethodList>
        {(newBankAccountDetailsLoading
          ? [...paymentSources, bankPlaceholderItem]
          : paymentSources
        ).map((source, _, arr) => {
          const isDefault = defaultPaymentSourceId
            ? defaultPaymentSourceId === source.id
            : false;
          const allowDeleteDefault =
            isDefault &&
            arr.filter(
              ({ paymentMethod }) => paymentMethod !== PaymentMethod.PAYPAL
            ).length === 1;
          const loadingIcon = (
            <LoadingIcon
              size={IconSize.X_SMALL}
              display={IconDisplay.SECONDARY}
            />
          );
          const sourceId = source.id;

          const deleteButton = (
            <DeletePaymentButton
              onDelete={() => {
                setPendingDeleteSourcesIds([
                  ...pendingDeleteSourcesIds,
                  ...(sourceId ? [sourceId] : []),
                ]);
                deletePayment(source);
              }}
              sourceDisplayName={`${source.brandName} ${
                source.displayId ? `*${source.displayId}` : ""
              }`}
              pendingDelete={
                !!sourceId && pendingDeleteSourcesIds.includes(sourceId)
              }
            />
          );
          const endElement =
            sourceId === bankPlaceholderItem.id ? (
              loadingIcon
            ) : isSelectingDefault || !defaultPaymentSourceId ? (
              selectedDefaultSourceId ? (
                selectedDefaultSourceId === sourceId ? (
                  loadingIcon
                ) : null
              ) : (
                <Button
                  data-tname={"paymentMethodSetDefault"}
                  data-bloom-block
                  role={ButtonRole.TEXT_ONLY}
                  onClick={{
                    kind: ButtonTargetKind.FUNCTION,
                    action: () => {
                      setSelectedDefaultSourceId(sourceId);
                    },
                  }}
                >
                  Set default
                </Button>
              )
            ) : isDefault && !allowDeleteDefault ? (
              <Button
                data-tname={"paymentMethodEditDefault"}
                role={ButtonRole.TEXT_ONLY}
                onClick={{
                  kind: ButtonTargetKind.FUNCTION,
                  action: () => {
                    setError(undefined);
                    setIsSelectingDefault(true);
                  },
                }}
              >
                Switch default
              </Button>
            ) : sourceId ? (
              deleteButton
            ) : null;

          if (source.status === PaymentSourceStatus.CONNECTED) {
            return (
              <li key={source.id}>
                <PaymentMethodItem
                  source={source}
                  suffix={
                    isDefault && !isSelectingDefault ? (
                      <SecondaryText>(default)</SecondaryText>
                    ) : undefined
                  }
                  end={endElement}
                />
              </li>
            );
          }

          return (
            !!sourceId && (
              <li key={source.id || undefined}>
                <AddBankPaymentItem
                  ref={setAddBankAccountBtn}
                  pendingBankSource={source}
                  pendingDeleteButton={deleteButton}
                  onCompletedAddingAccount={onCompletedAddingPaymentSource}
                  onPlaidConnectionSuccess={onPlaidConnectionSuccess}
                  onExit={onPlaidConnectExit}
                  itemId={sourceId}
                />
              </li>
            )
          );
        })}
        {!isSelectingDefault && (
          <React.Fragment>
            <li>
              <AddBankPaymentItem
                ref={setAddBankAccountBtn}
                onCompletedAddingAccount={onCompletedAddingPaymentSource}
                onPlaidConnectionSuccess={onPlaidConnectionSuccess}
                onExit={onPlaidConnectExit}
                itemId="new"
              />
            </li>
            <li>
              <AddCardPaymentItem
                onClick={() => {
                  setError(undefined);
                  setIsAddingCard(true);
                }}
              />
            </li>
          </React.Fragment>
        )}
      </PaymentMethodList>
      <SecondaryText>{USE_BANK_ACCOUNT_PROMPT}</SecondaryText>
    </React.Fragment>
  );
};

interface DeletePaymentButtonProps {
  onDelete: () => void;
  pendingDelete: boolean;
  sourceDisplayName: string;
}

const DeletePaymentButton: React.FCC<DeletePaymentButtonProps> = ({
  onDelete,
  pendingDelete,
  sourceDisplayName,
}) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  return (
    <React.Fragment>
      <Modal
        isOpen={showConfirmationModal}
        headerText={"Delete payment"}
        showHeader
        contentLabel="Delete payment Modal"
        onRequestClose={() => setShowConfirmationModal(false)}
      >
        <div css={verticalStackCss.l}>
          <p>
            Are you sure you want to delete this payment method (
            {sourceDisplayName})?
          </p>
          <div css={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              data-tname="deletePaymentAbort"
              role={ButtonRole.TEXT_ONLY}
              onClick={{
                kind: ButtonTargetKind.FUNCTION,
                action: () => setShowConfirmationModal(false),
              }}
            >
              Go back
            </Button>
            <Button
              data-tname="deletePaymentConfirm"
              data-bloom-block
              role={ButtonRole.PRIMARY}
              onClick={{
                kind: ButtonTargetKind.FUNCTION,
                action: (e) => {
                  e.stopPropagation();
                  onDelete();
                  setShowConfirmationModal(false);
                },
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
      <Button
        data-tname={"paymentMethodDelete"}
        role={ButtonRole.TEXT_ONLY}
        disabled={pendingDelete}
        submitting={pendingDelete}
        onClick={{
          kind: ButtonTargetKind.FUNCTION,
          action: () => setShowConfirmationModal(true),
        }}
      >
        Delete
      </Button>
    </React.Fragment>
  );
};

export default function PaymentPage() {
  const router = useEdoRouter();
  const donateOptions: {
    primarySlug: string;
    donationsEnabled: boolean;
    successUrl: string;
  } = useMemo(
    () => ({
      primarySlug: FUND_ACCOUNT_NONPROFIT_SLUG,
      donationsEnabled: true,
      successUrl: getRoutePath({
        name: ClientRouteName.FUND_ACCOUNT_CONFIRMATION,
        format: URLFormat.RELATIVE,
      }),
    }),
    []
  );

  const [givingCredits, setGivingCredits] = useState<
    PersonalGivingCreditResponse[] | undefined
  >(undefined);
  const [givingCreditsRemaining, setGivingCreditsRemaining] =
    useState<CurrencyValue>();
  const [fetchGivingCreditDataError, setFetchGivingCreditDataError] =
    useState<string>();
  const fetchGivingCreditsData = useCallback(() => fetchGivingCredits(), []);
  const handleGivingCreditsData = useCallback(
    ({
      credits,
      availableCreditAmount,
    }: {
      credits: PersonalGivingCreditResponse[];
      availableCreditAmount: CurrencyValue;
    }) => {
      setGivingCredits(credits);
      setGivingCreditsRemaining(availableCreditAmount);
    },
    []
  );
  const handleGivingCreditsDataError = useCallback((e: Error) => {
    setFetchGivingCreditDataError(
      getMessageForError(
        e,
        "An error occurred while fetching your giving credit information."
      )
    );
  }, []);
  useAsyncEffect({
    asyncOperation: fetchGivingCreditsData,
    handleResponse: handleGivingCreditsData,
    handleError: handleGivingCreditsDataError,
  });

  useEffect(() => {
    if (router.hash === `#${FUND_ACCOUNT_FRAGMENT_ID}`) {
      router.push(
        generateModalLink({
          ...donateOptions,
          router,
        })
      );
    }
    return;
  }, [donateOptions, router]);

  return (
    <ManagePageLayout
      pageTitle={"Payment methods"}
      selectedTab={ManagePageTab.Payment}
      pageContentCss={{ paddingTop: spacing.l }}
    >
      <PageSection>
        <div css={[verticalStackCss.xxl, { position: "relative" }]}>
          <NonprofitWarningCard />
          <FundAccountCard donateOptions={donateOptions} />
          <ManageStyledCard css={[verticalStackCss.l]}>
            <ManageSectionHeader
              title=""
              amount={givingCreditsRemaining?.amount}
              isLoaded={!!givingCreditsRemaining}
              amountDescription="Account balance"
              css={{ justifyContent: "left" }}
            />
            <div
              css={[
                cssForMediaSize({
                  min: MediaSize.LARGE,
                  css: horizontalStackCss.l,
                }),
                cssForMediaSize({
                  max: MediaSize.MEDIUM,
                  css: verticalStackCss.l,
                }),
              ]}
            >
              <div css={verticalStackCss.xs}>
                <ManageSectionHeader title="Payment methods" />
                <StyledCard>
                  <PaymentMethods />
                </StyledCard>
              </div>
              <GiftCards
                givingCredits={givingCredits}
                givingCreditsRemaining={givingCreditsRemaining}
                fetchGivingCreditDataError={fetchGivingCreditDataError}
              />
            </div>
          </ManageStyledCard>
        </div>
      </PageSection>
    </ManagePageLayout>
  );
}
